import { useState } from "react";
import { formatAmount } from "@/utils/formatAmount";
import { Link } from "react-router-dom";
import { Calendar, Gavel, MapPin, Paperclip, Search, Send, Trash2, Users } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useScopeCategories } from "@/features/rfq/api/scopes";
import { ProceedingFormModal } from "@/features/rfq/components/ProceedingFormModal";
import { PublishDialog } from "@/features/rfq/components/PublishDialog";
import {
  ACCESS_MODES,
  useDeleteProceeding,
  useProceedings,
  useProjectOptions,
  type Proceeding,
  type ProceedingFilters,
} from "@/features/rfq/api/proceedings";

/**
 * Postępowania ofertowe po stronie GK — lista, szkic, publikacja.
 *
 * Publikacja jest nieodwracalna w tym sensie, że ogłoszenia nie da się już zmienić:
 * oferenci mają widzieć te warunki, na które składali ofertę. Dlatego przed
 * publikacją pokazujemy, ile firm faktycznie zobaczy ogłoszenie — pusty krąg
 * odbiorców to najczęstszy powód postępowania bez ofert.
 * Sam formularz szkicu mieszka w ProceedingFormModal (współdzielony z panelem).
 */

function canManage(user: { is_ceo?: boolean; has_rfq_manage_permission?: boolean } | null) {
  return !!user?.is_ceo || !!user?.has_rfq_manage_permission;
}


export function ProceedingsPage() {
  const { user } = useAuthStore();
  const mayManage = canManage(user);
  usePageTitle("Postępowania ofertowe");

  const [filters, setFilters] = useState<ProceedingFilters>({ ordering: "-created" });
  const [search, setSearch] = useState("");
  const { data: proceedings, isLoading } = useProceedings({ ...filters, search });
  const { data: categories } = useScopeCategories();
  const { data: projects } = useProjectOptions();
  const remove = useDeleteProceeding();

  const [form, setForm] = useState<Partial<Proceeding> | null>(null);
  // Publikacja idzie przez ten sam ekran, co z panelu postępowania: zamrożenie treści
  // i wybór adresatów zaproszenia mailowego kupiec potwierdza raz, w jednym miejscu.
  const [publishing, setPublishing] = useState<string | null>(null);

  if (isLoading) return <PageLoader />;
  const list = proceedings ?? [];

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Postępowania ofertowe
          </h1>
          <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
          <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
            Zapytania, w których to my jesteśmy zamawiającym. Szkic można dowolnie
            zmieniać; po publikacji treść jest zamrożona, bo oferenci składają ofertę
            na konkretne warunki.
          </p>
        </div>
        {mayManage && (
          <Button
            onClick={() => setForm({})}
          >
            <Gavel size={14} className="mr-1.5 inline" /> Nowe postępowanie
          </Button>
        )}
      </div>

      {/* Wyszukiwarka + filtry + sortowanie */}
      <div className="mb-5 border-y border-[var(--color-border)] py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[14rem] flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj po numerze, tytule lub opisie…"
              className="w-full border border-[var(--color-border)] bg-transparent py-1.5 pl-8 pr-3 text-sm text-[var(--color-text-primary)]"
            />
          </div>
          <select
            value={filters.status ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value as ProceedingFilters["status"] })
            }
            className={selectClass}
          >
            <option value="">Wszystkie statusy</option>
            <option value="draft">Szkice</option>
            <option value="published">Opublikowane</option>
          </select>
          <select
            value={filters.access_mode ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                access_mode: e.target.value as ProceedingFilters["access_mode"],
              })
            }
            className={selectClass}
          >
            <option value="">Każdy tryb</option>
            {ACCESS_MODES.map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
          <select
            value={filters.ordering ?? "-created"}
            onChange={(e) => setFilters({ ...filters, ordering: e.target.value })}
            className={selectClass}
          >
            <option value="-created">Najnowsze</option>
            <option value="deadline">Termin ofert ↑</option>
            <option value="-deadline">Termin ofert ↓</option>
            <option value="title">Tytuł A–Z</option>
          </select>
          <select
            value={filters.project ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, project: e.target.value || undefined })
            }
            className={selectClass}
          >
            <option value="">Każde przedsięwzięcie</option>
            {(projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select
            value={filters.category ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, category: e.target.value || undefined })
            }
            className={selectClass}
          >
            <option value="">Każda kategoria</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
            <input
              type="checkbox"
              checked={!!filters.mine}
              onChange={(e) => setFilters({ ...filters, mine: e.target.checked })}
            />
            Moje
          </label>
          <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
            <input
              type="checkbox"
              checked={!!filters.open}
              onChange={(e) => setFilters({ ...filters, open: e.target.checked })}
            />
            Trwające
          </label>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="border border-dashed border-[var(--color-border)] px-6 py-12 text-center">
          <Gavel size={28} className="mx-auto mb-3 text-[var(--color-text-muted)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">Brak postępowań.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((proceeding) => (
            <article
              key={proceeding.id}
              className="border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-white p-4 dark:bg-gray-800"
            >
              {/* Nagłówek: tytuł niesie wagę, plakietki idą na prawo */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/rfq/proceedings/${proceeding.id}`}
                    className="text-base font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                  >
                    {proceeding.title}
                  </Link>
                  <p className="font-mono text-xs text-[var(--color-text-muted)]">
                    {proceeding.number}
                    {proceeding.responsible_name && ` · ${proceeding.responsible_name}`}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-1.5">
                  <Badge color={proceeding.status === "published" ? "green" : "gray"}>
                    {proceeding.status_label}
                  </Badge>
                  <Badge color="blue">{proceeding.access_mode_label}</Badge>
                  {proceeding.status === "published" && !proceeding.is_open_for_offers && (
                    <Badge color="yellow">Po terminie</Badge>
                  )}
                </div>
              </div>

              {/* Fakty w kolumnach — czyta się jak tabela, nie jak zdanie */}
              <div className="mt-3 grid gap-x-6 gap-y-2 border-t border-[var(--color-border)] pt-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                <Meta icon={<Calendar size={12} />} label="Oferty do">
                  {new Date(proceeding.submission_deadline).toLocaleString("pl", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </Meta>
                <Meta label="Realizacja do">
                  {proceeding.delivery_deadline
                    ? new Date(proceeding.delivery_deadline).toLocaleDateString("pl")
                    : "—"}
                </Meta>
                <Meta icon={<MapPin size={12} />} label="Województwo">
                  {proceeding.voivodeship_label || "bez znaczenia"}
                  {proceeding.county_labels.length > 0 && ` (${proceeding.county_labels.join(", ")})`}
                </Meta>
                <Meta label="Cena maksymalna">
                  {proceeding.max_price_net ? (
                    <>
                      {formatAmount(proceeding.max_price_net)} zł
                      <span className="ml-1 text-xs text-[var(--color-text-muted)]">
                        {proceeding.max_price_visible ? "jawna" : "ukryta"}
                      </span>
                    </>
                  ) : (
                    "—"
                  )}
                </Meta>
              </div>

              {proceeding.project_name && (
                <p className="mt-2 truncate text-xs text-[var(--color-text-muted)]">
                  Przedsięwzięcie: {proceeding.project_name}
                </p>
              )}

              {proceeding.positions_detail.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {proceeding.positions_detail.map((p) => (
                    <span
                      key={p.id}
                      className="border border-[var(--color-border)] px-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]"
                      title={p.category_name}
                    >
                      {p.name}
                    </span>
                  ))}
                </div>
              )}

              {proceeding.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-3">
                  {proceeding.attachments.map((attachment) => (
                    <ManagedFileLink
                      key={attachment.id}
                      file={attachment}
                      icon={<Paperclip size={12} />}
                    >
                      {attachment.label || attachment.original_filename}
                    </ManagedFileLink>
                  ))}
                </div>
              )}

              {mayManage && (
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--color-border)] pt-3">
                  <Link
                    to={`/rfq/proceedings/${proceeding.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                  >
                    <Users size={12} /> Panel postępowania
                  </Link>
                  {proceeding.status === "draft" && (
                    <>
                      <span className="text-[var(--color-border)]">|</span>
                      <button
                        onClick={() => setForm(proceeding)}
                        className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => setPublishing(proceeding.id)}
                        className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
                      >
                        <Send size={12} className="mr-0.5 inline" /> Opublikuj
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Usunąć szkic ${proceeding.number}?`))
                            remove.mutate(proceeding.id);
                        }}
                        className="ml-auto text-xs text-red-600 hover:underline"
                      >
                        <Trash2 size={12} className="mr-0.5 inline" /> Usuń
                      </button>
                    </>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Formularz */}
      {form && (
        <ProceedingFormModal initial={form} onClose={() => setForm(null)} />
      )}
      {publishing && (
        <PublishDialog
          proceedingId={publishing}
          onClose={() => setPublishing(null)}
        />
      )}
    </div>
  );
}

/** Filtry mają się mieścić w jednym rzędzie — stąd własna klasa zamiast nadpisywania w-full. */
const selectClass =
  "border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm text-[var(--color-text-primary)]";

function Meta({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {icon}
        {label}
      </p>
      <p className="text-[var(--color-text-primary)]">{children}</p>
    </div>
  );
}
